"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useYandexMap = void 0;
var _reactYandexMaps = require("@pbe/react-yandex-maps");
var _react = require("react");
var _useClusteredPickupPointsQuery = require("../api/useClusteredPickupPointsQuery");
var _shared = require("../components/shared");
var _useForceRedraw = require("./useForceRedraw");
var _usePickupPointsSearchConfiguration = require("./usePickupPointsSearchConfiguration");
const YMAPS_MAP_INSTANCE = 0;
const YMAPS_API = 1;
const YMAPS_ELEMENT_REFERENCE = 2;
const getArrayFromLocationCoordinates = (coordinates, fallbackCoordinates) => [coordinates?.latitude ?? fallbackCoordinates?.latitude ?? 0, coordinates?.longitude ?? fallbackCoordinates?.longitude ?? 0];
const getLocationCoordinatesFromArray = coordinates => ({
  latitude: coordinates[0] ?? 0,
  longitude: coordinates[1] ?? 0
});
const getClusteredPickupPointsQueryParams = (mapInstance, props) => {
  const bounds = mapInstance?.getBounds();
  const {
    isMapVisible,
    activeFilter,
    instantSearchTerm
  } = props;
  return {
    east: bounds?.[1]?.[1],
    isActive: isMapVisible,
    filter: activeFilter,
    north: bounds?.[1]?.[0],
    searchTerm: instantSearchTerm,
    south: bounds?.[0]?.[0],
    west: bounds?.[0]?.[1],
    zoom: mapInstance?.getZoom()
  };
};
const createYandexMap = (ymaps, {
  currentLocation,
  fallbackStartingLocation,
  initialZoom,
  element,
  onBoundsChange,
  onDeselect
}) => {
  const instance = new ymaps.Map(element, {
    center: getArrayFromLocationCoordinates(currentLocation, fallbackStartingLocation),
    zoom: initialZoom
  });
  instance.events.add('boundschange', onBoundsChange);
  instance.events.add('click', onDeselect);
  return instance;
};
const useYandexMapInstance = props => {
  const mapRef = (0, _react.useRef)(null);
  const ymaps = (0, _reactYandexMaps.useYMaps)(['Map', 'Placemark']);
  const redraw = (0, _useForceRedraw.useForceRedraw)();
  const mapInstance = (0, _react.useRef)();
  const {
    currentLocation,
    fallbackStartingLocation,
    initialZoom,
    onPickupPointItemSelection
  } = props;
  (0, _react.useEffect)(() => {
    if (!ymaps || !mapRef.current) {
      return;
    }
    if (mapInstance.current) {
      void mapInstance.current.setCenter(getArrayFromLocationCoordinates(currentLocation, fallbackStartingLocation));
    } else {
      mapInstance.current = createYandexMap(ymaps, {
        currentLocation,
        fallbackStartingLocation,
        initialZoom,
        element: mapRef.current,
        onBoundsChange: redraw,
        onDeselect: () => {
          onPickupPointItemSelection?.(undefined);
        }
      });
    }
  }, [currentLocation, fallbackStartingLocation, initialZoom, redraw, ymaps, onPickupPointItemSelection]);
  (0, _react.useEffect)(() => () => {
    mapInstance.current?.destroy();
  }, []);
  return [mapInstance.current, ymaps, mapRef];
};
const throwMapInitializationError = () => {
  throw new Error('Map methods called before initialization');
};
const DEFAULT_MAP_METHODS = {
  getViewBounds: throwMapInitializationError,
  getViewSettings: throwMapInitializationError,
  setLocationOrZoom: throwMapInitializationError,
  setViewBounds: throwMapInitializationError,
  zoomIn: throwMapInitializationError,
  zoomOut: throwMapInitializationError
};
const useYandexMapExposedMethods = mapItems => {
  const mapInstance = mapItems[YMAPS_MAP_INSTANCE];
  const {
    mapMethods,
    setViewSettings
  } = (0, _usePickupPointsSearchConfiguration.usePickupPointsSearchConfiguration)();
  const methods = (0, _react.useMemo)(() => mapInstance ? {
    getViewBounds: () => {
      const [sw = [], ne = []] = mapInstance.getBounds();
      const [north = 0, east = 0] = ne;
      const [south = 0, west = 0] = sw;
      return {
        east,
        north,
        south,
        west
      };
    },
    getViewSettings: () => ({
      zoom: mapInstance.getZoom(),
      location: getLocationCoordinatesFromArray(mapInstance.getCenter())
    }),
    setLocationOrZoom: options => {
      void mapInstance.setCenter(getArrayFromLocationCoordinates(options.location), options.zoom);
      setViewSettings({
        zoom: mapInstance.getZoom(),
        location: getLocationCoordinatesFromArray(mapInstance.getCenter())
      });
    },
    setViewBounds: (newBounds, padding) => void mapInstance.setBounds([[newBounds.south, newBounds.west], [newBounds.north, newBounds.east]], {
      zoomMargin: padding === undefined ? undefined : [padding, padding]
    }),
    zoomIn: () => void mapInstance.setZoom(mapInstance.getZoom() + 1),
    zoomOut: () => void mapInstance.setZoom(mapInstance.getZoom() - 1)
  } : DEFAULT_MAP_METHODS, [mapInstance, setViewSettings]);
  mapMethods.current = methods;
  return methods;
};
const useYandexMapPickupPoints = (mapItems, props, exposedMethods) => {
  const [mapInstance, ymaps] = mapItems;
  const pickupPointsQueryOptions = getClusteredPickupPointsQueryParams(mapInstance, props);
  const {
    clusteredPickupPointsList,
    isClusteredPickupPointsListLoading
  } = (0, _useClusteredPickupPointsQuery.useClusteredPickupPointsQuery)(pickupPointsQueryOptions);
  const {
    onPickupPointItemSelection,
    selectedPickupPoint
  } = props;
  const {
    setViewBounds
  } = exposedMethods;
  (0, _react.useEffect)(() => {
    if (!mapInstance || !ymaps) {
      return;
    }
    mapInstance.geoObjects.removeAll();
    clusteredPickupPointsList.forEach(point => {
      const {
        latitude,
        longitude
      } = point.coordinates;
      const position = [latitude, longitude];
      const {
        cluster,
        pickupPoint,
        displayedClusterSize
      } = point;
      const icon = Boolean(cluster) || !pickupPoint ? (0, _shared.getClusterMapIconSvgString)(displayedClusterSize, false, true) : (0, _shared.getPickupPointMapIconSvgString)(pickupPoint.deliveryMethod, pickupPoint.deliveryChannelId === selectedPickupPoint?.deliveryChannelId, true);
      const placemark = new ymaps.Placemark(position, {}, {
        iconImageHref: icon,
        iconLayout: 'default#image',
        iconImageSize: [60, 60]
      });
      placemark.events.add('click', cluster && !pickupPoint ? () => {
        const {
          lowerLeft: {
            latitude: south = 0,
            longitude: west = 0
          } = {},
          upperRight: {
            latitude: north = 0,
            longitude: east = 0
          } = {}
        } = cluster;
        setViewBounds({
          east,
          north,
          south,
          west
        });
      } : () => {
        const sanitizedPickupPoint = pickupPoint;
        onPickupPointItemSelection?.({
          ...sanitizedPickupPoint,
          coordinates: {
            latitude,
            longitude
          },
          deliveryFee: sanitizedPickupPoint.shippingFee
        });
      });
      mapInstance.geoObjects.add(placemark);
    });
  }, [clusteredPickupPointsList, mapInstance, onPickupPointItemSelection, selectedPickupPoint?.deliveryChannelId, setViewBounds, ymaps]);
  return isClusteredPickupPointsListLoading;
};
const useYandexMap = props => {
  const mapItems = useYandexMapInstance(props);
  const exposedMethods = useYandexMapExposedMethods(mapItems);
  const isClusteredPickupPointsListLoading = useYandexMapPickupPoints(mapItems, props, exposedMethods);
  const isYandexApiReady = !!mapItems[YMAPS_API];
  return [mapItems[YMAPS_ELEMENT_REFERENCE], isClusteredPickupPointsListLoading, isYandexApiReady];
};
exports.useYandexMap = useYandexMap;